<template>
  <div v-if="planMedia">
    <div class="entete">
      <div class="columns mgb-0">
        <div class="column pdb-0">
          <h1 class="title is-size-3">
            {{ $t('detailPlanMedia.title', { code: planMedia.code }) }}
          </h1>
        </div>
        <div class="column is-narrow pdb-0">
          <div class="is-pulled-right buttons boutonAction">
            <button
              @click="checkPermissions(permissions.maj) ? $router.push({
                name: 'm.presse.plan_media-edit',
                params: { id: planMedia.id },
              }) : showPopUpPermission()"
              :class="{'button-access-no-permission':
                !checkPermissions(permissions.maj)}"
              class="button is-addOrEdit">
              <span>
                {{ $t('general.button.edit') }}
              </span>
            </button>

            <dropdownButton
              type="is-addOrEdit"
              class="mgr-2"
              v-if="$route.params.id"
              :permission="permCommandes.maj"
              :actions="actionSecondary"
              @click="addDevisCommande(constantes.TYPE_COMMANDE)">
                {{ $t('addEditPlanMedia.commande.actions.addCommande') }}
            </dropdownButton>

          </div>
        </div>
      </div>
      <breadcrumb :breadcrumb="breadcrumb"/>
      <div class="tab">
        <ul>
          <li :class="{'is-active': activeTab === 'information'}"
              @click="activeTab = 'information'">
            {{ $t('detailPlanMedia.tab.information') }}
          </li>
          <li :class="{'is-active': activeTab === 'evenement'}"
              @click="activeTab = 'evenement'">
            {{ $t('detailPlanMedia.tab.evenement') }}
          </li>
          <li :class="{'is-active': activeTab === 'infoVentes'}"
              @click="activeTab = 'infoVentes'"
              >
            {{ $t('addEditPlanMedia.tab.commande') }}
          </li>
          <li :class="{'is-active': activeTab === 'commissions'}"
              @click="activeTab = 'commissions'">
            {{ $t('detailPlanMedia.tab.commissions') }}
          </li>
        </ul>
      </div>
    </div>
    <component
      :is="activeTab"
      :plan-media="planMedia"
      :recapitulatifMontant="recapitulatifMontant"
      detail
    />
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { PLAN_MEDIA } from '@/configuration/presse/permissionsPresseConf';
import dropdownButton from '@/components/general/button/dropdownButton';
import { get, getMontant } from '@/api/planMedia';
import messageErreur from '@/services/general/messageErreur';
import inProgress from '@/services/general/inProgress';
import Breadcrumb from '@/components/general/breadcrumb';
import Information from '@/components/planMedia/edition/m_information';
import Evenement from '@/components/planMedia/evenement';
import postGetPlanMedia from '@/services/planMedia/postGetPlanMedia';
import * as typesParametres from '@/store/modules/parametres-mutation-type';
import commissions from '@/components/planMedia/edition/commissions';
import infoVentes from '@/components/planMedia/infoVentes';
import { COMMANDES, DEVIS } from '@/configuration/permissionsBodacConf';
import { ECRAN_PLAN_MEDIA_DETAIL } from '@/configuration/bodac/planMedia/planMediaConfig';
import { TYPE_DEVIS, TYPE_COMMANDE } from '@/configuration/devisConf';

const Permissions = createNamespacedHelpers('permissions');
const Societe = createNamespacedHelpers('societe');
const Attachments = createNamespacedHelpers('attachments');
const Parametres = createNamespacedHelpers('parametres');

export default {
  name: 'c_detailPlanMedia',
  data() {
    return {
      permissions: PLAN_MEDIA,
      activeTab: 'information',
      recapitulatifMontant: null,
      permCommandes: COMMANDES,
      permDevis: DEVIS,
      planMedia: null,
      constantes: {
        TYPE_COMMANDE,
        TYPE_DEVIS,
      },
    };
  },
  components: {
    Breadcrumb,
    dropdownButton,
    Information,
    Evenement,
    commissions,
    infoVentes,
  },
  computed: {
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
    ...Parametres.mapGetters({
      parametre: 'parametre',
    }),
    breadcrumb() {
      return [
        { title: this.$i18n.t('detailPlanMedia.breadcrumb.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$i18n.t('detailPlanMedia.breadcrumb.liste'), route: { name: 'l.presse.plan_media' }, isActive: false },
        { title: this.$i18n.t('detailPlanMedia.breadcrumb.detail', { code: this.planMedia.code }), route: { name: '' }, isActive: true },
      ];
    },
    actionSecondary() {
      const actions = [
        {
          label: this.$t('addEditPlanMedia.commande.actions.addDevis'),
          action: () => { this.addDevisCommande(TYPE_DEVIS); },
          permissionAction: DEVIS.maj,
        },
      ];

      return actions;
    },
  },
  methods: {
    ...Attachments.mapActions({
      setAttachmentContext: 'setContext',
      setAttachmentElementId: 'setElementId',
    }),
    ...Permissions.mapActions({
      showPopUpPermission: 'showPopup',
    }),
    ...Parametres.mapActions({
      loadParametreUtilisateur: 'loadParametreUtilisateur',
    }),
    inProgress(message) {
      inProgress(this.$i18n, message);
    },
    addDevisCommande(type) {
      let nameRoute = '';
      if (type === TYPE_COMMANDE) nameRoute = 'm.bodac.commandes-planMedia';
      if (type === TYPE_DEVIS) nameRoute = 'm.bodac.devis-planMedia';
      this.$router.push({
        name: nameRoute,
        params: {
          planMediaId: this.planMedia.id,
          typeEcran: ECRAN_PLAN_MEDIA_DETAIL,
        },
      });
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.setAttachmentContext({ module: 'PlanMedia' });
      await this.loadParametreUtilisateur();
      const users = this.parametre(typesParametres.PARAMETRES_KEYS.utilisateur);
      if (this.$route.params.id) {
        const planMedia = (await get(this.$route.params.id)).data.result;
        await this.setAttachmentElementId({ id: planMedia.id });
        this.planMedia = postGetPlanMedia(planMedia, users);
        const index = this.planMedia.intervenants
          .findIndex(i => !i.nomCommercial);
        if (index !== -1) this.planMedia.intervenants.splice(index, 1);
        this.recapitulatifMontant = (await getMontant(this.$route.params.id)).data.result;
      }
      loadingComponent.close();
    } catch (e) {
      loadingComponent.close();
      messageErreur(this.$i18n);
      throw e;
    }
    this.setInformationPage(this.$t('pageGeneral.detail', { item: this.planMedia.code }));
  },
};
</script>
